<script lang="ts">
import { DoughnutChartConfig } from '@/types/MultiSeries';
import { compactNumber } from '@/utilities/Number';
import { paletteColors } from '@/utilities/Color';
import { DoughnutData, formatDoughnutChartData, FusionChartDoughnut2D } from '@/types/Doughnut';
import { ParentAccountCompany } from '@/types/Account';
import { PortcoProgram } from '@/types/Programs';
import { IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';

export default {
  name: 'ParentAccountOverview',
};

interface Month {
  label: string | null;
  programs: PortcoProgram[];
  key: string;
}

const chartDefault: DoughnutChartConfig = {
  animation: '1',
  centerLabelFontSize: 15,
  decimals: 0,
  doughnutRadius: '55%',
  forceYAxisValueDecimals: false,
  paletteColors: paletteColors,
  showBorder: false,
  showLegend: true,
  theme: 'cacs',
};

function premiumByCoverageTypeDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));
  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Coverage',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '$label <b>$$value</b>',
    },
    data: formatDoughnutChartData(data.map((d) => ({ ...d, displayValue: compactNumber(d.value) }))),
  };
}

function premiumByCarrierDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));

  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Carrier',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '<b>$label</b> $$value',
      captionFontSize: 20,
      captionAlignment: 'center',
    },
    data: formatDoughnutChartData(data.map((d) => ({ ...d, displayValue: compactNumber(d.value) }))),
  };
}

function premiumByCompanyDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));

  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Company',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '<b>$label</b> $$value',
      captionFontSize: 20,
      captionAlignment: 'center',
    },
    data: formatDoughnutChartData(data.map((d) => ({ ...d, displayValue: compactNumber(d.value) }))),
  };
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { parentAccountService, referenceService } from '@/services/api';
import { policyTypeService } from '@/services/PolicyTypeService';
import { useRoute } from 'vue-router';
import { MultiSeries } from '@/types/MultiSeries';
import Chip from '@/components/v2/Chip.vue';
import FilterBar from '@/components/v2/FilterBar.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { externalPolicyName, includeExternalPoliciesLabel } from '@/utilities/OrgConfig';
import { compactDisplayValueForDoughnutChart } from '@/types/Doughnut';
import { filters } from '@/utilities/Filters';
import { DateTime } from 'luxon';
import { useStore } from 'vuex';
import { LabelFilterType } from '@/types/components/FilterType';
import type { Account } from '@/types/Account';
import { AccountStatusProspect } from '@/utilities/Account';

const props = defineProps<{ account?: Account }>();

const route = useRoute();
const store = useStore<AppState>();

// -----
// data
// -----
const loading = ref(true);
const showExternal = ref(props.account?.accountStatus === AccountStatusProspect);
const showNonRecurring = ref(true);
const premiumByCoverageData = ref({} as MultiSeries);
const premiumByCarrierData = ref({} as MultiSeries);
const premiumByCompanyData = ref<DoughnutData[]>([]);
const premiumByCoverageOrCarrier = ref(0);
const companyData = ref<Array<ParentAccountCompany>>([]);
const expirationsByMonth = ref([] as Month[]);
const selectedCoverageTypes = ref<string[]>([]);
const coverageTypes = ref<LabelFilterType<string>[]>([]);
const coverageTypeToProductName = ref(new Map<number, string>());

// ---------
// computed
// ---------
const showExternalLabel = computed(() => includeExternalPoliciesLabel());

const premiumByCoverageType = computed(() => {
  return premiumByCoverageTypeDataSource(transformMultiSeriesToDoughnut(premiumByCoverageData.value));
});

const premiumByCarrier = computed(() => {
  return premiumByCarrierDataSource(transformMultiSeriesToDoughnut(premiumByCarrierData.value));
});

const premiumByCompany = computed(() => {
  return premiumByCompanyDataSource(premiumByCompanyData.value);
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// ---------
// watchers
// ---------
watch(showExternal, () => {
  refresh();
});

watch(showNonRecurring, () => {
  refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;

  const now = DateTime.local();
  const accountId = route.params.id as string;

  let queryParams = {} as Record<string, any>;
  if (selectedCoverageTypes.value) queryParams['coverageType'] = selectedCoverageTypes.value;

  queryParams['external'] = showExternal.value.toString();
  queryParams['showNonRecurring'] = showNonRecurring.value.toString();
  queryParams['runoff'] = 'true';
  queryParams['groupRunoff'] = 'true';

  const [coverageData, carrierData, allCompaniesData, companyPremiumData, programsExpiring, coverageTypesRefData, coverageTypesWithProducts] = await Promise.all([
    parentAccountService.premiumByCoverageType(accountId, { ...queryParams }),
    parentAccountService.premiumByCarrier(accountId, queryParams),
    parentAccountService.companies(accountId, queryParams),
    compactDisplayValueForDoughnutChart(parentAccountService.premiumByCompany(accountId, queryParams)),
    parentAccountService.programExpiration(
      accountId,
      now.toISODate(),
      now.plus({ years: 1 }).toISODate(),
      showExternal.value,
      selectedCoverageTypes.value,
    ),
    referenceService.coverageTypes(),
    policyTypeService.GetDistinctCoverageTypes().catch(() => []),
  ]);

  // Create mapping from coverage type to product name
  coverageTypeToProductName.value = (coverageTypesWithProducts || []).reduce((map, ct) => {
    if (ct && ct.coverageTypeId && ct.productName) {
      map.set(ct.coverageTypeId, ct.productName);
    }
    return map;
  }, new Map<number, string>());

  premiumByCoverageData.value = coverageData;
  premiumByCarrierData.value = carrierData;
  premiumByCompanyData.value = companyPremiumData;
  companyData.value = allCompaniesData
    .filter((c) => !c.isParent)
    .sort((c1, c2) => (c1.displayName || c1.accountName || '').localeCompare((c2.displayName || c2.accountName || '')));
  const parent = allCompaniesData.find((c) => c.isParent);
  if (parent) companyData.value.unshift(parent);

  // filter coverage types based on data on initial page load only
  if (coverageTypes.value.length === 0) {
    let coverageTypesFromData: string[] = [];
    coverageTypesFromData = [
      ...premiumByCoverageData.value.categories.flatMap(categoryList => categoryList?.category?.map(label => label.label) ?? '')
    ];

    coverageTypes.value = coverageTypesRefData.filter(e => {
      return coverageTypesFromData.includes(e.label);
    }).map(t => ({ label: t.label, value: t.label }));
  }

  const monthMap = programsExpiring.reduce((monthMap, pe) => {
    const expiresAt = DateTime.fromISO(pe.expiresAt, { zone: 'UTC' });
    const key = `${expiresAt.monthShort}-${expiresAt.year}`;

    if (!monthMap.has(key)) monthMap.set(key, { key, label: expiresAt.monthShort, programs: [] });
    monthMap.get(key)?.programs.push(pe);
    return monthMap;
  }, new Map<string, Month>());

  let n = now;
  let expirations = [] as Month[];

  let year = -1;
  for (let i = 0; i < 12; i++) {
    const key = `${n.monthShort}-${n.year}`;
    const month = monthMap.get(key) || { key, label: n.monthShort, programs: [] };

    // Only 1 month of each year displays year suffix:
    // FEB 2023  MAR  APR  ...  DEC  JAN 2024
    if (year !== n.year) {
      month.label += ` ${n.year}`;
      year = n.year;
    }

    // remove duplicates where multiple programs exist for a given month for a given portco and department
    month.programs = [
      ...month.programs
        .reduce((acc, p) => {
          const key = `${p.accountId}-${p.department}`;
          if (acc.has(key)) return acc;
          acc.set(key, p);
          return acc;
        }, new Map<string, PortcoProgram>())
        .values(),
    ];

    month.programs.sort((p1, p2) => p1.accountName.localeCompare(p2.accountName));
    expirations.push(month);

    n = n.plus({ month: 1 });
  }

  expirationsByMonth.value = [...expirations];

  loading.value = false;
}

function getProductNameForProgram(program: PortcoProgram): string {
  if (program.coverageTypeIds && program.coverageTypeIds?.length > 1) {
    return 'Multiple';
  }

  else if (program.coverageTypeIds && program.coverageTypeIds.length === 1) {
    // Try to map department to product name
    const productName = coverageTypeToProductName.value.get(program.coverageTypeIds[0]);
    if (productName) {
      return productName;
    }
  }
  
  // Fall back to department if no product name found
  return program.department;
}

function transformMultiSeriesToDoughnut(data: MultiSeries): DoughnutData[] {
  const categories = new Map<number, string>();

  if (data.categories) {
    data.categories[0].category?.forEach((cat, idx) => {
      categories.set(idx, cat.label);
    });
  }

  const totals = new Map<number, number>();
  data.dataset.forEach((dataset) => {
    dataset.data.forEach((data, idx) => {
      if (!totals.has(idx)) {
        totals.set(idx, 0);
      }
      totals.set(idx, (totals.get(idx) || 0) + (data.value || 0));
    });
  });

  return Array.from(totals.values()).map((value, idx) => ({ label: categories.get(idx), value: value }));
}

onMounted(() => {
  refresh();
});
</script>

<template>
  <div>
    <div class="flex w-full items-center gap-4 px-4 py-2">
      <GenericMultiFilter
        title="Coverage Type"
        v-model="selectedCoverageTypes"
        :options="coverageTypes"
        @update:model-value="refresh" />
      <FilterBar class="flex-1 justify-end">
        <div class="flex gap-2 mr-0 ml-auto">
          <Toggle id="show-non-recurring" v-model="showNonRecurring" label="Include Non-Recurring Policies" />
          <Toggle id="show-external" v-model="showExternal" :label="showExternalLabel" />
        </div>
      </FilterBar>
    </div>

    <PageSpinner v-if="loading" />

    <div v-if="!loading" class="inner">
      <div
        class="mb-4 grid grid-cols-3 justify-center gap-3 p-4 max-xl:grid-cols-2 max-sm:grid-cols-1"
      >
        <!-- doughnut charts-->
        <!-- premium by coverage or carrier chart-->
        <div class="flex flex-col justify-start px-1">
          <h3 class="mx-auto my-0">
            {{ premiumByCoverageOrCarrier === 0 ? 'Premium by Coverage' : 'Premium by Carrier' }}
          </h3>
          <div class="grow-0">
            <fusioncharts
              type="doughnut2d"
              width="100%"
              height="450"
              dataFormat="json"
              :dataSource="premiumByCoverageOrCarrier === 0 ? premiumByCoverageType : premiumByCarrier"
            />
          </div>
          <div class="join justify-center">
            <input
              type="radio"
              name="options"
              aria-label="Coverage"
              class="btn join-item btn-sm"
              @click="premiumByCoverageOrCarrier = 0"
              :checked="premiumByCoverageOrCarrier === 0"
            />

            <input
              type="radio"
              name="options"
              aria-label="Carrier"
              class="btn join-item btn-sm"
              @click="premiumByCoverageOrCarrier = 1"
              :checked="premiumByCoverageOrCarrier === 1"
            />
          </div>
        </div>

        <!-- premium company chart-->
        <div class="flex flex-col justify-start px-1">
          <h3 class="mx-auto my-0">Premium By Company</h3>
          <div class="grow-0">
            <fusioncharts
              type="doughnut2d"
              width="100%"
              height="450"
              dataFormat="json"
              :dataSource="premiumByCompany"
            />
          </div>
        </div>

        <!-- companies table -->
        <div class="flex flex-col">
          <template v-if="(companyData || []).length > 0">
            <div class="mb-4 flex justify-center">
              <h3 class="m-0 mx-auto">Companies</h3>
            </div>

            <div class="max-h-[400px] overflow-auto m-4 pt-0">
              <div class="relative">
                <table class="apptable dense">
                  <thead>
                    <tr>
                      <td class="p-4 nosort rounded-tl-md rounded-bl-md">Company</td>
                      <td class="p-4 nosort text-center">Client Since</td>
                      <td class="p-4 nosort">Industry</td>
                      <td class="p-4 nosort text-center rounded-tr-md rounded-br-md">Premium</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="company in companyData" :key="company.accountId">
                      <td>
                        <div class="text-sm">
                          <router-link
                            :to="`/account/${company.accountId}/overview`"
                            class="url-text link-hover text-sm text-black"
                            >{{ company.displayName || company.accountName }}</router-link
                          >
                        </div>
                        <Chip
                          v-if="company.accountStatus === 'P'"
                          :label="isUserAClient ? externalPolicyName() : 'Prospect'"
                          size="sm"
                          variant="solid"
                          color-class="bg-capital-blue text-white"
                          class="mt-1" />
                        <div v-if="company.domainName" class="link text-xs">
                          <a
                            v-if="company.domainName"
                            :href="'//' + `${company.domainName.trim()}`"
                            target="_blank"
                            rel="noopener noreferrer"
                            >{{ company.domainName }}</a
                          >
                        </div>
                      </td>
                      <td class="text-center">
                        <template v-if="company.clientSince && company.accountStatus !== 'P'">
                          {{ filters.date(company.clientSince) }}
                        </template>
                        <template v-else>&mdash;</template>
                      </td>
                      <td>{{ company.topLevelIndustry }}</td>
                      <td class="text-center">{{ filters.compactMoney(company.policyPremium) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </div>

      <h3 class="text-center">Upcoming Expirations</h3>

      <div class="mx-auto mb-4 mt-4 flex justify-center">
        <div
          class="mx-4 grid grid-cols-2 lg:grid-cols-4 lg:max-w-[1200px] 2xl:grid-cols-6 2xl:max-w-[1800px]  justify-center rounded-md border border-solid border-gray-300 lg:border-l-0 text-center"
        >
          <div
            v-for="(month, index) in expirationsByMonth"
            :key="month.key"
            class="min-h-48 border-0 border-solid border-gray-300"
						:class="{ 'border-r-1 lg:border-l-1': index % 2 === 0 }"
          >
            <div
              class="border-0 border-b-1 border-solid border-gray-300 bg-gray-100 p-1 py-2 text-xs font-bold uppercase text-gray-600"
            >
              {{ month.label }}
            </div>

            <div class="min-h-20 p-1 pb-4">
              <template v-for="program in month.programs" :key="program.accountId">
                <router-link :to="`/account/${program.accountId}/policy-detail`">
                  <div
                    class="m-1 rounded-md border border-(--chip-color) px-1 py-0.5 text-xs font-bold text-(--chip-color) hover:bg-blue-100"
                  >
                    {{ program.accountName }}: {{ getProductNameForProgram(program) }}
                  </div>
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
